/**
 * Rounding the way Excel does it.
 *
 * Excel holds a number as a binary double but rounds and prints it from at most 15 significant
 * decimal digits. So 1.005 (in binary 1.00499999999999989...) rounds to 1.01 at two decimals,
 * and 0.285 x 100 (in binary 28.499999999999996...) prints as 28.5%. JavaScript's `toFixed` and
 * `Math.round` work on the binary value and get both wrong; `Math.round(-2.5)` is -2 where Excel
 * says -3.
 *
 * The helpers here first snap the number to 15 significant digits, then round on that decimal
 * string. Nothing in a calculation is rounded: these serve the text formats and ROUNDUP.
 */
const SIGNIFICANT_DIGITS = 15;
/** More decimals than this cannot change a number of 15 significant digits. */
const MAX_DECIMALS = 30;
function clampDecimals(decimals) {
    if (!Number.isFinite(decimals))
        return 0;
    const whole = Math.trunc(decimals);
    if (whole > MAX_DECIMALS)
        return MAX_DECIMALS;
    if (whole < -MAX_DECIMALS)
        return -MAX_DECIMALS;
    return whole === 0 ? 0 : whole;
}
/** The 15 significant digits of a positive finite number, and the power of ten of the first. */
function significantDigits(positive) {
    // `toExponential` rounds the exact binary value to the asked digits, ties upward.
    const text = positive.toExponential(SIGNIFICANT_DIGITS - 1);
    const at = text.indexOf('e');
    return {
        digits: text.slice(0, at).replace('.', ''),
        exponent: Number(text.slice(at + 1)),
    };
}
function stripLeadingZeros(digits) {
    const stripped = digits.replace(/^0+/, '');
    return stripped === '' ? '0' : stripped;
}
/**
 * Round a number to `decimals` decimals from its 15 significant digits and return it as decimal
 * text. A number that is not finite comes back as zero; callers check for that first.
 */
export function roundDecimal15(x, decimals, mode) {
    const places = clampDecimals(decimals);
    if (!Number.isFinite(x) || x === 0) {
        return { negative: false, scaled: '0', decimals: places };
    }
    const { digits, exponent } = significantDigits(Math.abs(x));
    // The digit at index i stands for 10^(exponent - i). Keep every digit worth 10^-places or more.
    const keep = exponent + places + 1;
    let head;
    let rest;
    if (keep >= digits.length) {
        head = digits + '0'.repeat(keep - digits.length);
        rest = '';
    }
    else if (keep > 0) {
        head = digits.slice(0, keep);
        rest = digits.slice(keep);
    }
    else if (keep === 0) {
        head = '0';
        rest = digits;
    }
    else {
        // The first digit sits more than one place below the last kept decimal.
        head = '0';
        rest = `0${digits}`;
    }
    const bump = mode === 'half_away' ? rest.charAt(0) >= '5' : /[1-9]/.test(rest);
    const scaled = bump ? (BigInt(head) + 1n).toString() : stripLeadingZeros(head);
    return { negative: x < 0, scaled, decimals: places };
}
function toNumber(rounded) {
    const value = Number(`${rounded.scaled}e${-rounded.decimals}`);
    if (value === 0)
        return 0;
    return rounded.negative ? -value : value;
}
/** The number Excel works with when it rounds or prints: 15 significant digits. */
export function snap15(x) {
    if (!Number.isFinite(x))
        return x;
    const snapped = Number(x.toPrecision(SIGNIFICANT_DIGITS));
    return snapped === 0 ? 0 : snapped;
}
/**
 * Excel `ROUND(x, d)`: snap to 15 significant digits, then round half away from zero.
 * `roundHalfAway15(1.005, 2)` is 1.01 and `roundHalfAway15(-2.5, 0)` is -3.
 */
export function roundHalfAway15(x, decimals) {
    if (!Number.isFinite(x))
        return x;
    return toNumber(roundDecimal15(x, decimals, 'half_away'));
}
/**
 * Excel `ROUNDUP(x, d)`: away from zero, after snapping to 15 significant digits, so that float
 * noise does not add a unit: `roundUp(8.000000000000002, 0)` is 8, `roundUp(8.01, 0)` is 9.
 *
 * LibreOffice, which recalculates the workbook for the tests, snaps ROUNDUP at 12 significant
 * digits: a remainder between the 12th and the 15th digit (`8.000000000001`) rounds up here
 * and not there. A quotient of two amounts does not land in that band by itself.
 */
export function roundUp(x, decimals) {
    if (!Number.isFinite(x))
        return x;
    return toNumber(roundDecimal15(x, decimals, 'away'));
}
//# sourceMappingURL=round.js.map